import { OrderFormInfoData, RequisitionFormData } from '@/types/RequisitionFormTypes'
import { createOrder, getOrderByPatient, OrderStatus, updateOrder } from '@/utils/DatabaseOperations'
import { renderTestRequisitionPDF } from '@/utils/PDFRenderer'
import React, { useState } from 'react'
import Layout from '../Layout'
import RequisitionForm from '../RequisitionForm'

interface BaseOrderFormInfoProps {
  data: OrderFormInfoData | null
  loading?: boolean
  error?: string | null
  state?: string
}

const BaseOrderFormInfo: React.FC<BaseOrderFormInfoProps> = ({ data, loading = false, error = null }) => {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateOrderStatus = async (orderFormData: OrderFormInfoData, formData: RequisitionFormData) => {
    try {
      // Check if an order already exists for this patient
      const existingOrder = await getOrderByPatient(orderFormData.patient.id)

      if (existingOrder && existingOrder.id) {
        // Update existing order to complete status
        await updateOrder(existingOrder.id, orderFormData, formData, OrderStatus.COMPLETE)
      } else {
        // Create new order with complete status
        await createOrder(orderFormData, formData, OrderStatus.COMPLETE)
      }
    } catch (error) {
      console.error('Error updating order status:', error)
      throw new Error('Failed to update order status')
    }
  }

  const handleFormSubmit = async (formData: RequisitionFormData) => {
    if (!data) {
      setSubmitError('No data available for PDF generation')
      return
    }

    try {
      // First, update the order status to complete
      await updateOrderStatus(data, formData)

      const generatedPdfBytes = await renderTestRequisitionPDF(formData, data)

      // Create a blob from the PDF bytes and open in new window
      const blob = new Blob([generatedPdfBytes as BlobPart], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      // Open the PDF in a new window
      const newWindow = window.open(url, '_blank')

      if (!newWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.')
      }

      // Clean up the URL object after a delay to ensure the window has loaded
      setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 1000)

      // Don't show success page - stay on the form
      // The PDF is now open in a new window
    } catch (error) {
      console.error('Error generating PDF:', error)
      setSubmitError('Failed to generate PDF')
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8">
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
            <div className="mt-8">
              <div className="text-center">
                <div
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                >
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                    Loading...
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-600">Loading form information...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center p-4">
        <div className="w-full max-w-4xl rounded-lg bg-white p-4 shadow-lg">
          <div className="text-center">
            <p className="text-red-600">Error: {error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md p-8">
          <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-lg">
            <div className="mt-8">
              <div className="text-center">
                <p className="text-gray-600">No data available</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <RequisitionForm data={data} onSubmit={(formData: RequisitionFormData) => handleFormSubmit(formData)} />
      {submitError && (
        <div className="mt-4 rounded-lg bg-red-50 p-4">
          <p className="text-red-600">{submitError}</p>
        </div>
      )}
    </Layout>
  )
}

export default BaseOrderFormInfo
